import { useEffect, useState } from 'react';
import api from '../api/client';
import { useFincaId } from '../context/FincaContext';

const COLORES_MODO = {
  cronograma: '#0D47A1',
  sonico:     '#6A1B9A',
  hibrido:    '#EA580C',
};
const LABELS_MODO = {
  cronograma: 'Cronograma',
  sonico:     'Automático',
  hibrido:    'Híbrido',
};

export default function Dashboard() {
  const fincaIdContext = useFincaId();
  
  const [sectores, setSectores] = useState([]);
  const [piscinas, setPiscinas] = useState([]);
  const [resumenEquipos, setResumenEquipos] = useState(null);
  const [modos, setModos] = useState(null);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fincaIdContext]);

  const cargarDatos = async () => {
    setCargando(true);
    try {
      if (!fincaIdContext) return;

      const resSectores = await api.get(`/sectores/finca/${fincaIdContext}`);
      setSectores(resSectores.data || []);

      if (resSectores.data.length > 0) {
        const promesas = resSectores.data.map((s) =>
          api.get(`/piscinas/sector/${s.id}?incluirInactivas=true`)
            .then((res) => res.data || [])
            .catch(() => [])
        );
        const todas = (await Promise.all(promesas)).flat();
        setPiscinas(todas);
      }

      try {
        const res = await api.get('/dashboard/resumen-equipos');
        setResumenEquipos(res.data);
      } catch (error) {
        console.warn('Error resumen equipos:', error);
        setResumenEquipos(null);
      }

      try {
        const res = await api.get('/dashboard/modos-piscinas');
        setModos(res.data);
      } catch (error) {
        console.warn('Error modos piscinas:', error);
        setModos(null);
      }

    } catch (err) {
      console.error('Error cargando dashboard:', err);
    } finally {
      setCargando(false);
    }
  };

  if (cargando) return <div className="p-6 text-center text-gray-500">Cargando dashboard...</div>;

  const totalPiscinasModo = modos
    ? (modos.porModo.cronograma + modos.porModo.sonico + modos.porModo.hibrido)
    : 0;

  const modosArr = modos
    ? [
        { key: 'cronograma', label: LABELS_MODO.cronograma, valor: modos.porModo.cronograma, color: COLORES_MODO.cronograma },
        { key: 'sonico',     label: LABELS_MODO.sonico,     valor: modos.porModo.sonico,     color: COLORES_MODO.sonico },
        { key: 'hibrido',    label: LABELS_MODO.hibrido,    valor: modos.porModo.hibrido,    color: COLORES_MODO.hibrido },
      ]
    : [];

  return (
    <div className="p-6 h-full overflow-y-auto">
      <h1 className="text-3xl font-bold text-primary mb-6">Dashboard</h1>

      {/* KPIs Generales */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
          <div>
            <div className="text-xs text-gray-500 font-medium">Sectores</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">{sectores.length}</div>
          </div>
          <div className="text-4xl opacity-80">📁</div>
        </div>
        
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
          <div>
            <div className="text-xs text-gray-500 font-medium">Piscinas</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">{piscinas.length}</div>
          </div>
          <div className="text-4xl opacity-80">📈</div>
        </div>

        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex justify-between items-center">
          <div>
            <div className="text-xs text-gray-500 font-medium">Hectáreas Totales</div>
            <div className="text-3xl font-bold text-gray-800 mt-1">{resumenEquipos?.total_ha?.toFixed(2) || 0} Ha</div>
          </div>
          <div className="text-4xl opacity-80">🌾</div>
        </div>
      </div>

      {/* Equipos: Alimentadores y Aireadores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        
        {/* Alimentadores */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">⚙️</span>
            <h2 className="text-lg font-bold text-gray-700">Alimentadores</h2>
            <span className="ml-auto bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-sm font-bold">
              {resumenEquipos?.alimentadores_por_tecnologia?.reduce((a, b) => a + b.value, 0) || 0} en total
            </span>
          </div>
          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Por Tecnología</div>
            {resumenEquipos?.alimentadores_por_tecnologia?.length > 0 ? (
              resumenEquipos.alimentadores_por_tecnologia.map((item) => (
                <div key={item.name} className="flex justify-between items-center text-sm border-b border-gray-50 pb-1">
                  <span className="text-gray-600">{item.name}</span>
                  <span className="font-bold text-gray-800">{item.value}</span>
                </div>
              ))
            ) : <div className="text-sm text-gray-400 italic">Sin datos</div>}
          </div>
        </div>

        {/* Aireadores */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl">💨</span>
            <h2 className="text-lg font-bold text-gray-700">Aireadores</h2>
            <span className="ml-auto bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-sm font-bold">
              {resumenEquipos?.total_aireadores || 0} en total
            </span>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 p-3 rounded-lg text-center">
              <div className="text-xs text-blue-600 font-medium mb-1">HP / Hectárea</div>
              <div className="text-2xl font-bold text-blue-800">{resumenEquipos?.hp_por_ha?.toFixed(2) || 0}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg text-center">
              <div className="text-xs text-gray-600 font-medium mb-1">HP Totales</div>
              <div className="text-2xl font-bold text-gray-800">{resumenEquipos?.total_hp?.toFixed(1) || 0}</div>
            </div>
          </div>

          <div className="space-y-2">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Por Tipo</div>
            {resumenEquipos?.aireadores_por_tipo?.length > 0 ? (
              resumenEquipos.aireadores_por_tipo.map((item) => (
                <div key={item.name} className="flex justify-between items-center text-sm border-b border-gray-50 pb-1">
                  <span className="text-gray-600">{item.name}</span>
                  <span className="font-bold text-gray-800">{item.value}</span>
                </div>
              ))
            ) : <div className="text-sm text-gray-400 italic">Sin datos</div>}
          </div>
        </div>
      </div>

      {/* Modos de Alimentación */}
      {modos && (
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-700">🍤 Modos de Alimentación</h2>
            <span className="text-sm text-gray-500">
              {totalPiscinasModo} / {modos.total} piscinas activas con registro
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {modosArr.map((m) => {
              const pct = totalPiscinasModo > 0 ? (m.valor / totalPiscinasModo) * 100 : 0;
              return (
                <div
                  key={m.key}
                  className="flex flex-col items-center justify-center p-4 rounded-lg border"
                  style={{ borderColor: m.color + '40' }}
                >
                  <div
                    className="w-full h-1.5 rounded-t-full mb-2"
                    style={{ backgroundColor: m.color }}
                  />
                  <div className="text-sm font-medium text-gray-700 mb-1">{m.label}</div>
                  <div className="text-3xl font-bold" style={{ color: m.color }}>
                    {m.valor}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{pct.toFixed(1)}% del total</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}